import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Disc3, Mail, Shield } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AuthPage({ mode = "login" }) {
    const isSignup = mode === "signup";
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { applySession } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [error, setError] = useState(searchParams.get("error") || "");
    const [submitting, setSubmitting] = useState(false);

    const copy = useMemo(() => ({
        eyebrow: isSignup ? "CREATE ACCOUNT" : "WELCOME BACK",
        title: isSignup ? "Join Outlapped." : "Log in to Outlapped.",
        subtitle: isSignup
            ? "Save your race dashboard preferences and keep your replay workspace ready."
            : "Continue into your race intelligence workspace.",
        action: isSignup ? "Create Account" : "Log In",
        alternate: isSignup ? "Already have an account?" : "Need an account?",
        alternateLink: isSignup ? "/login" : "/signup",
        alternateCta: isSignup ? "Log in" : "Sign up",
    }), [isSignup]);

    async function onSubmit(event) {
        event.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const payload = isSignup
                ? { email, password, display_name: displayName }
                : { email, password };
            const response = isSignup
                ? await api.authSignup(payload)
                : await api.authLogin(payload);
            applySession(response.token, response.user);
            navigate("/replay", { replace: true });
        } catch (err) {
            setError(err?.response?.data?.detail || "Authentication failed.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="relative min-h-[calc(100svh-4rem)] overflow-hidden pt-28">
            <div className="absolute inset-0 grid-bg opacity-[0.18]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_70%_20%,rgba(225,6,0,0.16),transparent_62%)]" />
            <div className="relative mx-auto grid max-w-6xl gap-10 px-4 pb-20 sm:px-6 lg:grid-cols-[1fr_480px] lg:px-8">
                <div className="flex flex-col justify-center">
                    <div className="label-eyebrow text-f1-red">{copy.eyebrow}</div>
                    <h1 className="mt-5 font-display text-6xl leading-[0.92] tracking-tight sm:text-7xl">
                        {copy.title}
                    </h1>
                    <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-400">
                        {copy.subtitle}
                    </p>
                    <div className="mt-10 grid max-w-xl gap-3 text-sm text-zinc-400 sm:grid-cols-3">
                        {["Race replay", "Saved preferences", "Provider-backed data"].map((item) => (
                            <div key={item} className="border-l border-f1-red/50 pl-3">
                                {item}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="glass-strong rounded-[28px] border border-white/10 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.38)] sm:p-8">
                    <form onSubmit={onSubmit} className="space-y-4">
                        {isSignup ? (
                            <label className="block">
                                <span className="label-eyebrow text-zinc-500">Name</span>
                                <input
                                    value={displayName}
                                    onChange={(event) => setDisplayName(event.target.value)}
                                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition-colors focus:border-f1-red/60"
                                    placeholder="Your name"
                                    autoComplete="name"
                                />
                            </label>
                        ) : null}
                        <label className="block">
                            <span className="label-eyebrow text-zinc-500">Email</span>
                            <input
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition-colors focus:border-f1-red/60"
                                placeholder="you@example.com"
                                type="email"
                                autoComplete="email"
                                required
                            />
                        </label>
                        <label className="block">
                            <span className="label-eyebrow text-zinc-500">Password</span>
                            <input
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition-colors focus:border-f1-red/60"
                                placeholder={isSignup ? "At least 8 characters" : "Your password"}
                                type="password"
                                autoComplete={isSignup ? "new-password" : "current-password"}
                                required
                            />
                        </label>

                        {error ? (
                            <div className="rounded-xl border border-f1-red/30 bg-f1-red/10 px-4 py-3 text-sm text-red-200">
                                {error}
                            </div>
                        ) : null}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-f1-red px-5 py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition-all hover:bg-white hover:text-graphite-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting ? "Working..." : copy.action}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </button>
                    </form>

                    <div className="my-6 flex items-center gap-3">
                        <div className="h-px flex-1 bg-white/10" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                            or
                        </span>
                        <div className="h-px flex-1 bg-white/10" />
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <a
                            href={api.oauthStartUrl("google")}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:border-f1-red/50 hover:bg-f1-red/10"
                        >
                            <Mail className="h-4 w-4" />
                            Google
                        </a>
                        <a
                            href={api.oauthStartUrl("discord")}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:border-f1-red/50 hover:bg-f1-red/10"
                        >
                            <Disc3 className="h-4 w-4" />
                            Discord
                        </a>
                    </div>

                    <div className="mt-6 flex items-center justify-between gap-4 text-sm text-zinc-500">
                        <span>{copy.alternate}</span>
                        <Link to={copy.alternateLink} className="font-medium text-white hover:text-f1-red">
                            {copy.alternateCta}
                        </Link>
                    </div>

                    <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-xs leading-relaxed text-zinc-500">
                        <Shield className="mt-0.5 h-4 w-4 flex-shrink-0 text-f1-red" />
                        Accounts are stored in your Outlapped MongoDB database. Google and Discord login activate after their OAuth env vars are configured.
                    </div>
                </div>
            </div>
        </div>
    );
}
